import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Use current origin for API calls to work in both preview and production
const API = `${window.location.origin}/api`;

const AuthContext = createContext(null);

// Set axios auth header from localStorage on load
const storedToken = localStorage.getItem('session_token');
if (storedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuth = useCallback(async () => {
    // Check if we have a token in localStorage first
    const token = localStorage.getItem('session_token');
    
    if (!token) {
      setUser(null);
      setLoading(false);
      setAuthChecked(true);
      return null;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, { 
        withCredentials: true,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUser(response.data);
      setAuthChecked(true);
      return response.data;
    } catch (error) {
      console.error('Auth check failed:', error?.response?.status, error?.response?.data);
      // Only clear user if we get 401 (invalid/expired token)
      if (error?.response?.status === 401) {
        setUser(null);
        // Clear invalid token
        localStorage.removeItem('session_token');
        document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure';
      }
      setAuthChecked(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Set user directly (used by AuthCallback after successful login)
  const setAuthUser = useCallback((userData, token) => {
    if (token) {
      // Store in localStorage (primary auth method)
      localStorage.setItem('session_token', token);
      
      // Also try to set cookie (backup for same-origin requests)
      try {
        document.cookie = `session_token=${token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=None; Secure`;
      } catch (e) {
        console.warn('Could not set cookie:', e);
      }
      
      // Update axios default headers immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    setUser(userData);
    setLoading(false);
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      // Call backend logout to invalidate session
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear localStorage token
      localStorage.removeItem('session_token');
      localStorage.removeItem('qa_session');
      // Clear cookie
      document.cookie = 'session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=None; Secure';
      // Clear axios default header
      delete axios.defaults.headers.common['Authorization'];
      // Reset state
      setUser(null);
      setAuthChecked(false);
      // Redirect to home
      window.location.href = '/';
    }
  };

  const value = {
    user,
    loading,
    logout,
    checkAuth,
    setAuthUser,
    isAuthenticated: !!user,
    authChecked
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
